import { keepPreviousData, useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import type { ContentTextDTO, ContentTypeDTO, ContentVersionDTO } from '@today/types'
import type { TextFiltersView, TextRowView, TextsListViewProps } from '@today/ui-domain'
import { useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { contentService } from '@/services/content.service'
import { day } from './content-ui'
import {
	apiFilters,
	filtersFromSearch,
	inForceSummary,
	listParams,
	localeChips,
	localPage,
	searchFromFilters,
	type TextListFilters,
	usageByText,
	usageLabel,
} from './text-list-rules'
import {
	contentKeys,
	useAllAssignments,
	useContentLocales,
	useContentT,
	useContentTypes,
	useContentViewer,
	useNow,
} from './useContentQueries'

const EMPTY: ContentTextDTO[] = []

/** The page (server-paged, or every page filtered to unused texts and paged locally). */
function useTextPage(filters: TextListFilters, unusedIds: ((id: string) => boolean) | null, now: Date) {
	const paged = useQuery({
		queryKey: [...contentKeys.texts, 'page', listParams(filters, now)] as const,
		queryFn: () => contentService.listTexts(listParams(filters, now)),
		enabled: !filters.unused,
		placeholderData: keepPreviousData,
	})
	const all = useQuery({
		queryKey: [...contentKeys.texts, 'all', apiFilters(filters, now)] as const,
		queryFn: () => contentService.allTexts(apiFilters(filters, now)),
		enabled: filters.unused,
	})
	if (!filters.unused) {
		return { rows: paged.data?.data ?? EMPTY, pageCount: paged.data?.totalPages ?? 0, query: paged }
	}
	const unused = unusedIds ? (all.data ?? EMPTY).filter((text) => unusedIds(text.id)) : EMPTY
	const local = localPage(unused, filters.page)
	return { rows: local.data, pageCount: local.totalPages, query: all }
}

const toRow = (
	text: ContentTextDTO,
	type: ContentTypeDTO | undefined,
	versions: ContentVersionDTO[] | undefined,
	usage: TextRowView['usage'],
	now: Date,
): TextRowView => {
	const inForce = versions ? inForceSummary(versions, now) : null
	return {
		id: text.id,
		code: text.code,
		name: text.name,
		typeName: type?.name ?? text.typeCode,
		chips: localeChips(type?.requiredLocales ?? [], text.latestVersion, now),
		inForce: inForce && {
			version: inForce.version,
			until: inForce.until && day(inForce.until),
			scheduled: inForce.scheduled && { version: inForce.scheduled.version, from: day(inForce.scheduled.from) },
		},
		usage,
	}
}

/** Archive: confirmation, DELETE, then the list and assignments reload. */
function useArchive(rows: readonly ContentTextDTO[]) {
	const client = useQueryClient()
	const { errorMessage } = useContentT()
	const [archiving, setArchiving] = useState<string | null>(null)
	const archive = useMutation({
		mutationFn: (id: string) => contentService.archiveText(id),
		onSuccess: async () => {
			setArchiving(null)
			await client.invalidateQueries({ queryKey: contentKeys.texts })
			await client.invalidateQueries({ queryKey: contentKeys.assignments })
		},
	})
	const target = rows.find((r) => r.id === archiving)
	return {
		archiving: target ? { code: target.code } : null,
		archiveError: archive.error ? errorMessage(archive.error) : null,
		isBusy: archive.isPending,
		onArchive: (id: string) => {
			archive.reset()
			setArchiving(id)
		},
		onConfirmArchive: () => archiving && archive.mutate(archiving),
		onCancelArchive: () => setArchiving(null),
	}
}

/** The texts list (TO-420 content-admin-ui REQ-002): URL filters, derived columns, archive. */
export function useTextList(): TextsListViewProps {
	const { t } = useContentT()
	const navigate = useNavigate()
	const [search, setSearch] = useSearchParams()
	const filters = filtersFromSearch(search)
	const { caps } = useContentViewer()
	const types = useContentTypes()
	const assignments = useAllAssignments()
	const usage = useMemo(() => usageByText(assignments.data ?? []), [assignments.data])
	const unusedIds = assignments.data ? (id: string) => usageLabel(usage.get(id)).unused : null
	const now = useNow()
	const page = useTextPage(filters, unusedIds, now)
	const versions = useQueries({
		queries: page.rows.map((row) => ({
			queryKey: contentKeys.versions(row.id),
			queryFn: () => contentService.versions(row.id),
		})),
	})
	const { locales } = useContentLocales()
	const archive = useArchive(page.rows)
	const typeByCode = new Map((types.data ?? []).map((type) => [type.code, type]))
	const setFilters = (next: TextListFilters) => setSearch(searchFromFilters(next))
	return {
		t,
		rows: page.rows.map((row, i) =>
			toRow(row, typeByCode.get(row.typeCode), versions[i]?.data, usageLabel(usage.get(row.id)), now),
		),
		filters: filters satisfies TextFiltersView,
		types: (types.data ?? []).map((type) => ({ id: type.code, label: type.name })),
		locales,
		page: filters.page,
		pageCount: page.pageCount,
		empty: page.query.isSuccess && page.rows.length === 0,
		error: page.query.isError,
		...archive,
		onFilterChange: (key, value) => setFilters({ ...filters, [key]: value, page: 1 }),
		onPageChange: (next) => setFilters({ ...filters, page: next }),
		onOpen: (id) => void navigate(`/content/texts/${id}`),
		onRetry: () => void page.query.refetch(),
		onCreate: caps.write ? () => void navigate('/content/texts/new') : undefined,
		onArchive: caps.write ? archive.onArchive : undefined,
	}
}
